import { cssBundleHref } from '@remix-run/css-bundle';
import {
  isRouteErrorResponse,
  Link,
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunction,
  useLoaderData,
  useRouteError,
  MetaFunction } from
'@remix-run/react';
import stylesheet from './tailwind.css';
import { Header } from './components/header/Header';
import {
  DataFunctionArgs,
  json,
  LinksFunction } from
'@remix-run/server-runtime';
import { getCollections } from '~/providers/collections/collections';
import { activeChannel, channels } from '~/providers/channel/channel';
import {
  APP_META_DESCRIPTION,
  APP_META_TITLE,
  CHANNEL_TOKEN_COOKIE_KEY } from
'~/constants';
import { useEffect, useState } from 'react';
import { CartTray } from '~/components/cart/CartTray';
import { getActiveCustomer } from '~/providers/customer/customer';
import { getPages } from './providers/pages/pages';
import Footer from '~/components/footer/Footer';
import { useActiveOrder } from '~/utils/use-active-order';
import { useChangeLanguage } from 'remix-i18next';
import { useTranslation } from 'react-i18next';
import { getI18NextServer } from '~/i18next.server';
import { OrderDetailFragment } from './generated/graphql';
import VerificationPopup from './components/verification-popup/VerificationPopup';
import { getSessionStorage } from './sessions';
import { AccordionFaqs } from '~/providers/faqs/faqs';

export const meta: MetaFunction = () => {
  return [{ title: APP_META_TITLE }, { description: APP_META_DESCRIPTION }];
};

export const links: LinksFunction = () => [
{ rel: 'stylesheet', href: stylesheet },
...(cssBundleHref ? [{ rel: 'stylesheet', href: cssBundleHref }] : []),
{
  rel: 'stylesheet',
  href: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap'
}];


const devMode =
typeof process !== 'undefined' && process.env.NODE_ENV === 'development';

// The root data does not change once loaded.
export const shouldRevalidate: ShouldRevalidateFunction = ({
  nextUrl,
  currentUrl,
  formAction
}) => {
  if (currentUrl.pathname === '/sign-in') {
    // just logged in
    return true;
  }
  if (currentUrl.pathname === '/account' && nextUrl.pathname === '/') {
    // just logged out
    return true;
  }
  if (formAction === '/checkout/payment') {
    // submitted payment for order
    return true;
  }
  return false;
};

export type RootLoaderData = {
  activeCustomer: Awaited<ReturnType<typeof getActiveCustomer>>;
  collections: Awaited<ReturnType<typeof getCollections>>;
  pages: {id: string;title: string;slug: string;}[];
  locale: string;
  channels: Awaited<ReturnType<typeof channels>>;
  activeChannelCode: string;
  faqs: NonNullable<Awaited<ReturnType<typeof AccordionFaqs>>>;
};

export async function loader({ request }: DataFunctionArgs) {
  const [collections, activeCustomer, pages, publicChannels, faqs] =
  await Promise.all([
  getCollections(request, { take: 30 }),
  getActiveCustomer({ request }),
  getPages({ request }),
  channels({ request }),
  AccordionFaqs({ request })]
  );

  const topLevelCollections = collections.filter(
    (collection) => collection.parent?.name === '__root_collection__'
  );

  const session = await getSessionStorage().then((sessionStorage) =>
  sessionStorage.getSession(request.headers.get('Cookie'))
  );

  const activeChannelCode = session.get(CHANNEL_TOKEN_COOKIE_KEY);

  const locale = await getI18NextServer().then((i18next) =>
  i18next.getLocale(request)
  );
  const loaderData: RootLoaderData = {
    activeCustomer,
    collections: topLevelCollections,
    pages: pages?.items ?? [],
    channels: publicChannels,
    locale,
    activeChannelCode,
    faqs: faqs ?? []
  };

  return json(loaderData, { headers: activeCustomer._headers });
}

export default function App() {
  const [open, setOpen] = useState(false);
  const loaderData = useLoaderData<RootLoaderData>();

  const { locale, channels, activeChannelCode } =
  useLoaderData<typeof loader>();

  const { i18n } = useTranslation();
  const {
    activeOrderFetcher,
    activeOrder,
    adjustOrderLine,
    removeItem,
    refresh
  } = useActiveOrder();

  useChangeLanguage(locale);

  useEffect(() => {
    // When the loader has run, this implies we should refresh the contents
    // of the activeOrder as the user may have signed in or out.
    refresh();
  }, [loaderData]);

  return (
    <html lang={locale} dir={i18n.dir()} id="app">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <link rel="icon" href="/favicon.ico" type="image/png"></link>
        <Meta />
        <Links />
      </head>
      <body>
        {!activeChannelCode && <VerificationPopup channels={channels} />}

        <Header
          onCartIconClick={() => setOpen(!open)}
          cartQuantity={activeOrder?.totalQuantity ?? 0} />

        <main className="">
          <Outlet
            context={{
              activeOrderFetcher,
              activeOrder,
              adjustOrderLine,
              removeItem
            }} />

        </main>
        <CartTray
          open={open}
          onClose={setOpen}
          activeOrder={(activeOrder as OrderDetailFragment)}
          adjustOrderLine={adjustOrderLine}
          removeItem={removeItem} />

        <ScrollRestoration />
        <Scripts />
        <Footer />

        {devMode && <LiveReload />}
      </body>
    </html>);

}

type DefaultSparseErrorPageProps = {
  tagline: string;
  headline: string;
  description: string;
};

/**
 * You should replace this in your actual storefront to provide a better user experience.
 * You probably want to still show your footer and navigation. You will also need fallbacks
 * for your data dependant components in case your shop instance / CMS isnt responding.
 * See: https://remix.run/docs/en/main/route/error-boundary
 */
function DefaultSparseErrorPage({
  tagline,
  headline,
  description
}: DefaultSparseErrorPageProps) {
  return (
    <html lang="en" id="app">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <link rel="icon" href="/favicon.ico" type="image/png"></link>
        <Meta />
        <Links />
      </head>
      <body>
        <main className="flex flex-col items-center px-4 py-16 sm:py-32 text-center">
          <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            {tagline}
          </span>
          <h1 className="mt-2 font-bold text-gray-900 tracking-tight text-4xl sm:text-5xl">
            {headline}
          </h1>
          <p className="mt-4 text-base text-gray-500 max-w-full break-words">
            {description}
          </p>
          <div className="mt-6">
            <Link
              to="/"
              className="text-base font-medium text-primary-600 hover:text-primary-500 inline-flex gap-2">

              Go back home
            </Link>
          </div>
        </main>
        <ScrollRestoration />
        <Scripts />
        {devMode && <LiveReload />}
      </body>
    </html>);

}

/**
 * As mentioned in the jsdoc for `DefaultSparseErrorPage` you should replace this to suit your needs.
 */
export function ErrorBoundary() {
  let tagline = 'Oopsy daisy';
  let headline = 'Unexpected error';
  let description = "We couldn't handle your request. Please try again later.";

  const error = useRouteError();
  if (isRouteErrorResponse(error)) {
    tagline = `${error.status} error`;
    headline = error.statusText;
    description = error.data;
  }

  return (
    <DefaultSparseErrorPage
      tagline={tagline}
      headline={headline}
      description={description} />);


}

/**
 * In Remix v2 there will only be a `ErrorBoundary`
 * As mentioned in the jsdoc for `DefaultSparseErrorPage` you should replace this to suit your needs.
 * Relevant for the future: https://remix.run/docs/en/main/route/error-boundary-v2
 */
export function CatchBoundary() {
  return ErrorBoundary();
}